import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import { REHYDRATE } from "redux-persist";

import store, { IAppState } from "../Store";
import Datastore from "../../communication/Datastore";
import { ImportLog } from "../../types/core/ImportLog";
import { unwrappedDispatch } from "../../foundation/Dispatching";
import Faculty from "../../types/core/Faculty";

// Define the type for our state store entry
type FacultyState = {
    archives: Faculty[];
    faculties: Faculty[];
    flat: Faculty[];
    status: string;
    error: string;
};

// Define the initial state for our state store entry
const initialState: FacultyState = {
    archives: [],
    faculties: [],
    flat: [],
    status: "idle",
    error: null,
};

// Create a slice to hold all our reducers
const facultySlice = createSlice({
    name: "faculty",
    initialState: initialState,
    reducers: {
        resetFacultyStatus(state) {
            state.status = "idle";
        },
    },
    extraReducers(builder) {
        builder
            .addCase(REHYDRATE, (_, rawData: any) => {
                if (rawData?.key !== "tts") return undefined;
                return {
                    archives:
                        rawData?.payload?.faculty?.archives?.map(
                            c => new Faculty(c),
                        ) || [],
                    faculties:
                        rawData?.payload?.faculty?.faculties?.map(
                            c => new Faculty(c),
                        ) || [],
                    flat:
                        rawData?.payload?.faculty?.flat?.map(
                            c => new Faculty(c),
                        ) || [],
                    status: !rawData?.payload?.faculty?.faculties
                        ? "idle"
                        : "succeeded",
                    error: null,
                };
            })
            .addCase(fetchFaculties.pending, (state, action) => {
                state.status = "loading";
            })
            .addCase(fetchFaculties.fulfilled, (state, action) => {
                state.status = "succeeded";
                // Add any fetched posts to the array
                state.faculties = action.payload[0].map(c => new Faculty(c));
                state.archives = action.payload[1].map(c => new Faculty(c));

                const temp = state.faculties.filter(c => !c.faculties?.length);
                const main = state.faculties.filter(c => c.faculties?.length);
                for (const entry of main)
                    for (const faculty of entry.faculties)
                        faculty.isChild = true;
                state.faculties = main.concat([
                    new Faculty({
                        id: -1,
                        code: "[Ungrouped]",
                        name: "[Ungrouped]",
                        faculties: temp,
                        order: 9999,
                    }),
                ]);

                state.flat = [];
                for (const faculty of state.faculties) {
                    if (faculty.id !== -1) {
                        const temp = new Faculty(faculty);
                        temp.faculties = [];
                        state.flat.push(temp);
                    }
                    if (faculty.faculties?.length)
                        state.flat = state.flat.concat(
                            faculty.faculties.map(c => new Faculty(c)),
                        );
                }
            })
            .addCase(fetchFaculties.rejected, (state, action) => {
                state.status = "failed";
                state.error = action.error.message;
            })
            .addCase(saveFaculty.pending, (state, action) => {
                state.status = "loading";
            })
            .addCase(saveFaculty.fulfilled, (state, action) => {
                state.status = "succeeded";
                // Add any fetched posts to the array
                for (const faculty of action.payload) {
                    let parent = state.faculties.find(c =>
                        c.faculties?.find(f => f.id === faculty.id),
                    );
                    if (parent) {
                        if (parent.id === -1 && faculty.faculties?.length) {
                            parent = new Faculty({
                                ...parent,
                                faculties: parent.faculties.filter(
                                    f => f.id !== faculty.id,
                                ),
                            });
                            state.faculties = state.faculties
                                .filter(c => c.id !== parent.id)
                                .concat(parent)
                                .concat(new Faculty(faculty));
                            for (const child of faculty.faculties)
                                parent.faculties = parent.faculties.filter(
                                    f => f.id !== child.id,
                                );
                        } else {
                            parent = new Faculty({
                                ...parent,
                                faculties: parent.faculties
                                    .filter(f => f.id !== faculty.id)
                                    .concat(new Faculty(faculty)),
                            });
                            state.faculties = state.faculties
                                .filter(c => c.id !== parent.id)
                                .concat(parent);
                        }
                    } else {
                        const existing = state.faculties.find(
                            c => c.id === faculty.id,
                        );
                        if (existing) {
                            const newChildren =
                                faculty.faculties?.map(f => f.id) ?? [];
                            const children = existing.faculties.filter(
                                f => !newChildren.includes(f.id),
                            );
                            state.faculties = state.faculties.concat(children);
                        }
                        state.faculties = state.faculties
                            .filter(c => c.id !== faculty.id)
                            .concat(new Faculty(faculty));
                    }
                }

                const ungrouped = state.faculties.find(c => c.id === -1);
                const temp = state.faculties
                    .filter(c => !c.faculties?.length)
                    .concat(ungrouped?.faculties ?? []);
                const main = state.faculties.filter(
                    c => c.faculties?.length && c.id !== -1,
                );
                for (const entry of main)
                    for (const faculty of entry.faculties) {
                        faculty.isChild = true;

                        const tempChild = temp.find(t => t.id === faculty.id);
                        if (tempChild) temp.remove(tempChild);
                    }
                state.faculties = main.concat([
                    new Faculty({
                        id: -1,
                        code: "[Ungrouped]",
                        name: "[Ungrouped]",
                        faculties: temp,
                        order: 9999,
                    }),
                ]);

                state.flat = [];
                for (const faculty of state.faculties) {
                    if (faculty.id !== -1) {
                        const temp = new Faculty(faculty);
                        temp.faculties = [];
                        state.flat.push(temp);
                    }
                    if (faculty.faculties?.length)
                        state.flat = state.flat.concat(
                            faculty.faculties.map(c => new Faculty(c)),
                        );
                }
            })
            .addCase(saveFaculty.rejected, (state, action) => {
                state.status = "failed";
                state.error = action.error.message;
            })
            .addCase(archiveFaculty.pending, (state, action) => {
                state.status = "loading";
            })
            .addCase(archiveFaculty.fulfilled, (state, action) => {
                state.status = "succeeded";
                // Add any fetched posts to the array
                const facultyIds = action.payload.map(c => c.id);
                state.faculties = state.faculties
                    .filter(c => !facultyIds.includes(c.id))
                    .map(c => {
                        if (c.faculties?.length) {
                            c.faculties = c.faculties.filter(
                                f => !facultyIds.includes(f.id),
                            );
                        }
                        return c;
                    });

                state.archives = state.archives.concat(
                    action.payload.map(c => new Faculty(c)),
                );

                const ungrouped = state.faculties.find(c => c.id === -1);
                const temp = state.faculties
                    .filter(c => !c.faculties?.length)
                    .concat(ungrouped?.faculties ?? []);
                const main = state.faculties.filter(
                    c => c.faculties?.length && c.id !== -1,
                );
                for (const entry of main)
                    for (const faculty of entry.faculties)
                        faculty.isChild = true;
                state.faculties = main.concat([
                    new Faculty({
                        id: -1,
                        code: "[Ungrouped]",
                        name: "[Ungrouped]",
                        faculties: temp,
                        order: 9999,
                    }),
                ]);

                state.flat = [];
                for (const faculty of state.faculties) {
                    if (faculty.id !== -1) {
                        const temp = new Faculty(faculty);
                        temp.faculties = [];
                        state.flat.push(temp);
                    }
                    if (faculty.faculties?.length)
                        state.flat = state.flat.concat(
                            faculty.faculties.map(c => new Faculty(c)),
                        );
                }
            })
            .addCase(archiveFaculty.rejected, (state, action) => {
                state.status = "failed";
                state.error = action.error.message;
            });
    },
});

const { resetFacultyStatus } = facultySlice.actions;

export const initialiseFacultyDetails = createAsyncThunk(
    "faculty/initialiseFacultyDetails",
    (_, thunkAPI) => thunkAPI.dispatch(fetchFaculties()),
);

// This is a thunk, they are used to load the state
export const fetchFaculties = createAsyncThunk(
    "faculty/fetchFaculties",
    () =>
        Promise.all([
            Datastore.getType<Faculty[]>("api/Faculties"),
            Datastore.getType<Faculty[]>("api/Faculties/Archive"),
        ]),
    {
        condition: (_, api) =>
            store.getState().db.faculty.status === "idle",
    },
);
export const saveFaculty = createAsyncThunk(
    "faculty/saveFaculty",
    async (payload: Faculty[]) => {
        const adds = payload.filter(c => !(c.id > 0));
        const upds = payload.filter(c => c.id > 0);
        const waits = [];

        if (adds.length > 0)
            waits.push(Datastore.postType<Faculty[]>("api/Faculties", adds));
        if (upds.length > 0)
            waits.push(Datastore.putType<Faculty[]>("api/Faculties", upds));
        const results = await Promise.all(waits);
        return results[0]?.concat(results[1] ?? []) ?? [];
    },
    {
        condition: (_, api) =>
            store.getState().db.faculty.status === "succeeded",
    },
);
export const archiveFaculty = createAsyncThunk(
    "faculty/archiveFaculty",
    (payload: Faculty[]) => {
        Datastore.post(
            "api/Faculties/Archive",
            payload.filter(c => c.id !== -1).map(c => c.id),
        );
        return payload;
    },
    {
        condition: (_, api) =>
            store.getState().db.faculty.status === "succeeded",
    },
);

/*******     Importing     *******/
export const importFacultiesFromTD = async (id: number) => {
    const result = await Datastore.postType<ImportLog>(
        `api/Faculties/Import/TD/${id}`,
    );

    store.dispatch(resetFacultyStatus());
    await unwrappedDispatch(fetchFaculties());

    return result;
};

// We have to export the reducer as this is added to the state store
export default facultySlice.reducer;
